import React from 'react';
import '../assets/Reports.css';
import Card from '../reports/Card';
import SingleStatCard from '../reports/SingleStatCard';
import CompactPieChart from '../reports/CompactPieChart';
import HistogramChart from '../reports/HistogramChart';
import HorizontalStatsCard from '../reports/HorizontalStatsCard';
import BootKpiTable from '../reports/BootKpiTable';
import TimeRangeSelector from '../reports/TimeRangeSelector';
import { LoadingSkeleton } from '../reports/LoadingSkeleton';
import FleetHealthSection from './FleetHealthSection';
import deviceDataService from '../../services/deviceDataService';
import ThroughputStatsSection from '../reports/ThroughputStatsSection';

function AggregatedDeviceView({ onDeviceClick }) {
    // Local state management
    const [selectedTimeRange, setSelectedTimeRange] = React.useState('7d');
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    // Throughput state is loaded independently from the legacy dashboard cache.
    const [throughput, setThroughput] = React.useState({ data: null, loading: false, error: null });

    // Fleet health state
    const [fleetHealth, setFleetHealth] = React.useState({ data: null, loading: false, error: null });

    // Scroll-based time selector visibility
    const [isTimeSelectorVisible, setIsTimeSelectorVisible] = React.useState(true);
    const lastScrollY = React.useRef(0);

    // Load aggregate data
    const loadAggregateData = React.useCallback(async (timeRange) => {
        try {
            setLoading(true);
            setError(null);
            console.log('🔄 Loading aggregate data for time range:', timeRange);

            const aggregateData = await deviceDataService.getAggregateDashboard(timeRange);
            
            // Check if this is a cache error response
            if (aggregateData?.cacheError) {
                console.log('📭 Cache error detected:', aggregateData.message);
                setData(null); // Clear any existing data
                setError({
                    type: 'cache_error',
                    message: aggregateData.message,
                    details: aggregateData.details,
                    timeRange: aggregateData.timeRange
                });
            } else {
                setData(aggregateData);
                console.log('✅ Aggregate data loaded successfully');
            }
        } catch (err) {
            console.error('❌ Error loading aggregate data:', err);
            setError({
                type: 'network_error',
                message: err.message,
                details: 'An unexpected error occurred while loading data.'
            });
        } finally {
            setLoading(false);
        }
    }, []);

    // Load fleet health data
    const fetchFleetHealth = React.useCallback(async (timeRange) => {
        try {
            setFleetHealth(prev => ({ ...prev, loading: true, error: null }));
            const result = await deviceDataService.getFleetHealth(timeRange);
            if (result?.cacheError) {
                setFleetHealth({ data: null, loading: false, error: { message: result.message } });
            } else {
                setFleetHealth({ data: result, loading: false, error: null });
            }
        } catch (err) {
            setFleetHealth({ data: null, loading: false, error: { message: err.message } });
        }
    }, []);

    const fetchThroughput = React.useCallback(async (timeRange) => {
        try {
            setThroughput(prev => ({ ...prev, loading: true, error: null }));
            const result = await deviceDataService.getThroughputStats(timeRange);
            setThroughput({ data: result, loading: false, error: null });
        } catch (err) {
            setThroughput({ data: null, loading: false, error: { message: err.message } });
        }
    }, []);

    // Load data on mount and when time range changes
    React.useEffect(() => {
        loadAggregateData(selectedTimeRange);
        fetchFleetHealth(selectedTimeRange);
        fetchThroughput(selectedTimeRange);
    }, [selectedTimeRange, loadAggregateData, fetchFleetHealth, fetchThroughput]);

// Time range handler
const handleTimeRangeChange = (range) => {
    setSelectedTimeRange(range);
};

    // Refresh handler
    const handleRefresh = () => {
        loadAggregateData(selectedTimeRange);
        fetchThroughput(selectedTimeRange);
    };

    // Scroll detection for time selector visibility
    React.useEffect(() => {
        let scrollableElement = null;
        let cleanupListeners = [];
        
        const handleScroll = (event) => {
            const currentScrollY = event.target.scrollTop || window.scrollY;
            const scrollThreshold = 5; // Small threshold to prevent jitter
            
            // Only update if scroll difference is significant
            if (Math.abs(currentScrollY - lastScrollY.current) < scrollThreshold) return;
            
            if (currentScrollY > lastScrollY.current && currentScrollY > 20) {
                // Scrolling down - hide time selector
                setIsTimeSelectorVisible(false);
            } else if (currentScrollY < lastScrollY.current) {
                // Scrolling up - show time selector
                setIsTimeSelectorVisible(true);
            }
            
            lastScrollY.current = currentScrollY;
        };
        
        // Find the actual scrollable element
        setTimeout(() => {
            const allElements = document.querySelectorAll('*');
            
            for (let el of allElements) {
                if (el.scrollHeight > el.clientHeight) {
                    const computedStyle = window.getComputedStyle(el);
                    const overflowY = computedStyle.overflowY;
                    
                    if (overflowY === 'scroll' || overflowY === 'auto') {
                        scrollableElement = el;
                        break;
                    }
                }
            }
            
            // Add scroll listener to the found element or fallback to window
            if (scrollableElement) {
                scrollableElement.addEventListener('scroll', handleScroll, { passive: true });
                cleanupListeners.push(() => scrollableElement.removeEventListener('scroll', handleScroll));
            } else {
                // Fallback to window/document
                window.addEventListener('scroll', handleScroll, { passive: true });
                document.addEventListener('scroll', handleScroll, { passive: true });
                cleanupListeners.push(() => window.removeEventListener('scroll', handleScroll));
                cleanupListeners.push(() => document.removeEventListener('scroll', handleScroll));
            }
        }, 100);
        
        return () => {
            cleanupListeners.forEach(cleanup => cleanup());
        };
    }, []); // Empty dependency array to avoid re-running

    // Render loading skeleton
    const renderLoadingSkeleton = () => (
        <div className="reports-container">
            <Card title="Overview" collapsible={true}>
                <div className="overview-stats-grid">
                    <LoadingSkeleton height="120px" />
                </div>
            </Card>

            <Card title="Network Health" collapsible={true}>
                <div className="network-health-pie-charts">
                    <LoadingSkeleton height="200px" />
                    <LoadingSkeleton height="200px" />
                    <LoadingSkeleton height="200px" />
                    <LoadingSkeleton height="200px" />
                    <LoadingSkeleton height="200px" />
                </div>
                <div className="network-health-signal-grid">
                    <LoadingSkeleton height="300px" />
                    <LoadingSkeleton height="300px" />
                    <LoadingSkeleton height="300px" />
                </div>
            </Card>

            <Card title="Boot KPI" collapsible={true}>
                <div className="boot-kpi-charts">
                    <LoadingSkeleton height="300px" />
                    <LoadingSkeleton height="300px" />
                </div>
            </Card>
        </div>
    );

    // Render error state
    const renderError = () => {
        if (error?.type === 'cache_error') {
            return (
                <div className="reports-container">
                    <Card title="📊 Aggregate Dashboard" collapsible={false}>
                        <div className="cache-error-message" style={{ 
                            textAlign: 'center', 
                            padding: '40px 20px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef'
                        }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                            <h3 style={{ color: '#495057', marginBottom: '12px' }}>Data Not Available</h3>
                            <p style={{ color: '#6c757d', marginBottom: '8px', fontSize: '16px' }}>
                                {error.message}
                            </p>
                            {error.details && (
                                <p style={{ color: '#868e96', fontSize: '14px', marginBottom: '20px' }}>
                                    {error.details}
                                </p>
                            )}
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                <button 
                                    onClick={() => handleTimeRangeChange('1d')}
                                    style={{ 
                                        padding: '8px 16px', 
                                        backgroundColor: selectedTimeRange === '1d' ? '#007bff' : '#f8f9fa',
                                        color: selectedTimeRange === '1d' ? 'white' : '#495057',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    1 Day
                                </button>
                                <button 
                                    onClick={() => handleTimeRangeChange('7d')}
                                    style={{ 
                                        padding: '8px 16px', 
                                        backgroundColor: selectedTimeRange === '7d' ? '#007bff' : '#f8f9fa',
                                        color: selectedTimeRange === '7d' ? 'white' : '#495057',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    7 Days
                                </button>
                                <button 
                                    onClick={() => handleTimeRangeChange('14d')}
                                    style={{ 
                                        padding: '8px 16px', 
                                        backgroundColor: selectedTimeRange === '14d' ? '#007bff' : '#f8f9fa',
                                        color: selectedTimeRange === '14d' ? 'white' : '#495057',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    14 Days
                                </button>
                                <button 
                                    onClick={() => handleTimeRangeChange('30d')}
                                    style={{ 
                                        padding: '8px 16px', 
                                        backgroundColor: selectedTimeRange === '30d' ? '#007bff' : '#f8f9fa',
                                        color: selectedTimeRange === '30d' ? 'white' : '#495057',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    30 Days
                                </button>
                            </div>
                        </div>
                    </Card>
                </div>
            );
        }

        // Network or other errors
        return (
            <div className="reports-container">
                <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    <div>
                        <strong>Error loading aggregate data:</strong>
                        <br />
                        {error?.message || error}
                        <br />
                        {error?.details && (
                            <>
                                <small style={{ color: '#666' }}>{error.details}</small>
                                <br />
                            </>
                        )}
                        <button onClick={handleRefresh} style={{ marginTop: '10px', padding: '5px 10px' }}>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="aggregate-device-report">
            {/* Time Range Selector */}
            <div className={`aggregate-time-selector ${!isTimeSelectorVisible ? 'hidden' : ''}`}>
                <TimeRangeSelector
                    selectedRange={selectedTimeRange}
                    onRangeChange={handleTimeRangeChange}
                    disabled={loading}
                    onRefresh={handleRefresh}
                    isRefreshing={loading}
                    preset="aggregate"
                />
            </div>

            {/* Content */}
            {loading && !data ? (
                renderLoadingSkeleton()
            ) : error ? (
                renderError()
            ) : (
                <div className="reports-container">
                    {/* Overview Section */}
                    <Card title="Overview" collapsible={true}>
                        <div className="overview-stats-grid">
                            <SingleStatCard
                                title="Total Devices Seen"
                                value={data?.overview?.totalDevices || 0}
                                subtitle="Devices seen"
                                color="#1a73e8"
                            />
                        </div>
                    </Card>

                    {/* Fleet Health Section */}
                    <FleetHealthSection
                        data={fleetHealth.data}
                        loading={fleetHealth.loading}
                        error={fleetHealth.error}
                        onDeviceClick={onDeviceClick}
                    />

                    <ThroughputStatsSection
                        data={throughput.data}
                        loading={throughput.loading}
                        error={throughput.error}
                        timeRange={selectedTimeRange}
                    />

                    {/* Network Health Section */}
                    <Card title="Network Health" collapsible={true}>
                        {/* Pie Charts - 4 charts in 2x2 grid */}
                        <div className="network-health-pie-charts">
                            <CompactPieChart
                                title="NAS Registration Status"
                                data={data?.networkHealth?.registrationStatus?.data || []}
                                labels={data?.networkHealth?.registrationStatus?.labels || []}
                                colors={data?.networkHealth?.registrationStatus?.colors || []}
                            />
                            <CompactPieChart
                                title="NAS Registration Failure Reasons"
                                data={data?.networkHealth?.registrationFailures?.data || []}
                                labels={data?.networkHealth?.registrationFailures?.labels || []}
                                colors={data?.networkHealth?.registrationFailures?.colors || []}
                            />
                            <CompactPieChart
                                title="PDN Status"
                                data={data?.networkHealth?.pdnStatus?.data || []}
                                labels={data?.networkHealth?.pdnStatus?.labels || []}
                                colors={data?.networkHealth?.pdnStatus?.colors || []}
                            />
                            <CompactPieChart
                                title="PDN Failure Reasons"
                                data={data?.networkHealth?.pdnFailureReasons?.data || []}
                                labels={data?.networkHealth?.pdnFailureReasons?.labels || []}
                                colors={data?.networkHealth?.pdnFailureReasons?.colors || []}
                            />
                            <CompactPieChart
                                title="PDN APN Type Distribution"
                                data={data?.networkHealth?.pdnApnTypeDistribution?.data || []}
                                labels={data?.networkHealth?.pdnApnTypeDistribution?.labels || []}
                                colors={data?.networkHealth?.pdnApnTypeDistribution?.colors || []}
                            />
                        </div>
                        
                        {/* Signal Strength Histograms - Same Row */}
                        <div className="network-health-signal-histograms">
                            <HistogramChart
                                title="Signal Strength (RSRP)"
                                bins={data?.networkHealth?.rsrpHistogram?.bins || []}
                                xAxisLabel="RSRP (dBm)"
                                yAxisLabel="Number of Instances"
                                color="#3498db"
                            />
                            <HistogramChart
                                title="Signal Strength (RSRQ)"
                                bins={data?.networkHealth?.rsrqHistogram?.bins || []}
                                xAxisLabel="RSRQ (dB)"
                                yAxisLabel="Number of Instances"
                                color="#e74c3c"
                            />
                            <HistogramChart
                                title="Signal Strength (SINR)"
                                bins={data?.networkHealth?.sinrHistogram?.bins || []}
                                xAxisLabel="SINR (dB)"
                                yAxisLabel="Number of Instances"
                                color="#2ecc71"
                            />
                        </div>
                    </Card>

                    {/* Boot KPI Section */}
                    <Card title="Boot KPI" collapsible={true}>
                        {/* Boot KPI Type Pie Chart */}
                        <div className="boot-kpi-pie-row">
                            <CompactPieChart
                                title="Boot KPI Type Distribution"
                                data={data?.bootKpi?.kpiDistribution?.data || []}
                                labels={data?.bootKpi?.kpiDistribution?.labels || []}
                                colors={data?.bootKpi?.kpiDistribution?.colors || []}
                            />
                        </div>
                        
                        {/* Boot KPI Timing Statistics Table */}
                        <BootKpiTable timingHistograms={data?.bootKpi?.timingHistograms || {}} />
                    </Card>
                </div>
            )}
        </div>
    );
}

export default AggregatedDeviceView;
